import { useCallback, useEffect, useState, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { Place } from '@/types';

export interface UsePeerCitiesArgs {
  selectedPlace: Place | null;
  initialPeers?: Place[];
}

export interface UsePeerCitiesResult {
  peerCities: Place[];
  reservedPeers: Place[];
  loadingPeers: boolean;
  peersError: string | null;
  addPeer: (peer: Place) => void;
  removePeer: (placeFips: string) => void;
  replacePeers: (newPeers: Place[]) => void;
}

/**
 * Manages peer-city list for a selected jurisdiction. Fetches a default peer
 * list via the `find-peers` Supabase function on first load OR whenever the
 * selected place changes and no initial peers were supplied.
 */
export const usePeerCities = (
  { selectedPlace, initialPeers = [] }: UsePeerCitiesArgs
): UsePeerCitiesResult => {
  const [peerCities, setPeerCities] = useState<Place[]>(initialPeers);
  const [reservedPeers, setReservedPeers] = useState<Place[]>([]);
  const [loadingPeers, setLoadingPeers] = useState(false);
  const [peersError, setPeersError] = useState<string | null>(null);
  // Track first mount to decide whether to skip initial fetch when peers are supplied via props (e.g. URL override or saved comparison)
  const isFirstRender = useRef(true);
  // Track fetch version so we can ignore out-of-date responses
  const fetchVersionRef = useRef(0);

  // NEW: Sync peer list if initialPeers prop changes after first render (e.g., when URL params finish loading)
  useEffect(() => {
    if (initialPeers && initialPeers.length > 0 && peerCities.length === 0) {
      console.log('[usePeerCities] Detected change in initialPeers, syncing peer list');
      // Invalidate any in-flight fetch so its response is ignored
      fetchVersionRef.current += 1;

      setPeerCities(initialPeers);
      // Update reserved peers if more than 4 provided
      if (initialPeers.length > 4) {
        setReservedPeers(initialPeers.slice(4));
      } else {
        setReservedPeers([]);
      }
    }
  }, [initialPeers, peerCities.length]);

  // Fetch default peers when selected place changes and we don't already have
  // peers (e.g. not supplied via URL).
  useEffect(() => {
    console.log('[usePeerCities] Effect triggered with selectedPlace:', selectedPlace);
    
    if (!selectedPlace || !selectedPlace.place_fips) {
      console.log('[usePeerCities] No selected place, clearing state');
      setPeerCities([]);
      setReservedPeers([]);
      setLoadingPeers(false);
      setPeersError(null);
      return;
    }

    // On first render, if peers were supplied externally, keep them and
    // skip default peer fetch so we faithfully reproduce the shared URL or
    // saved state.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (initialPeers.length > 0) {
        console.log('[usePeerCities] Initial peers supplied, skipping default fetch');
        return;
      }
    }

    // Clear any existing peers when the selected place changes so we can
    // load a fresh peer list.
    console.log('[usePeerCities] Clearing existing peers for new selection');
    setPeerCities([]);

    const fetchPeers = async () => {
      // Increment version for this fetch call
      const currentVersion = ++fetchVersionRef.current;
      try {
        console.log('[usePeerCities] Starting peer fetch for:', {
          place: selectedPlace.name,
          fips: selectedPlace.place_fips
        });
        
        setLoadingPeers(true);
        setPeersError(null);

        const { data, error } = await supabase.functions.invoke(
          'find-peers',
          {
            body: {
              place: selectedPlace.name,
              fips: parseInt(selectedPlace.place_fips, 10),
              mode: 'default' // Explicitly set mode for peer finding
            },
          }
        );

        console.log('[usePeerCities] Received response:', { data, error });

        if (error) throw error;

        if (Array.isArray(data)) {
          // The data from the function has 'Place' and 'place_fips' (number)
          // We need to map it to our canonical Place type.
          const formattedPeers = data.map(peer => ({
            name: peer.Place || peer.name,
            place_fips: String(peer.place_fips)
          }));
          
          console.log('[usePeerCities] Setting formatted peers:', formattedPeers);
          
          // Ignore if a newer fetch or initialPeers sync has occurred
          if (currentVersion === fetchVersionRef.current) {
            setPeerCities(formattedPeers.slice(0, 4));
            setReservedPeers(formattedPeers.slice(4));
          }
        } else {
          console.log('[usePeerCities] Received non-array data:', data);
          setPeersError('Invalid response format from server');
        }
      } catch (err: any) {
        console.error('[usePeerCities] Error invoking find-peers function:', err);
        setPeersError('Could not calculate peer cities.');
      } finally {
        setLoadingPeers(false);
      }
    };

    fetchPeers();
  }, [selectedPlace?.name, selectedPlace?.place_fips]);

  // ------- Mutators ------- //
  const removePeer = useCallback((placeFips: string) => {
    console.log('[usePeerCities] Removing peer:', placeFips);
    setPeerCities((prev) => prev.filter((p) => p.place_fips !== placeFips));
  }, []);

  const addPeer = useCallback(
    (peer: Place) => {
      console.log('[usePeerCities] Adding peer:', peer);
      setPeerCities((prev) => {
        if (
          prev.length >= 10 ||
          prev.some(
            (p) => p.place_fips === peer.place_fips
          )
        ) {
          console.log('[usePeerCities] Skipping add - limit reached or duplicate');
          return prev;
        }
        return [...prev, peer];
      });
    },
    []
  );

  // Replace entire peer list (used when loading from saved comparison)
  const replacePeers = useCallback((newPeers: Place[]) => {
    console.log('[usePeerCities] Replacing peer list with:', newPeers);
    setPeerCities(newPeers);
  }, []);

  return {
    peerCities,
    reservedPeers,
    loadingPeers,
    peersError,
    addPeer,
    removePeer,
    replacePeers,
  };
}; 